import { formatDateToMMDDYYYY } from "utils/date";
import { checkForAndReplaceNullEmptyWhitespace, convertToTitleCase } from "utils/string";
import { getFileNameBasedOnCurrentDateTime } from "utils/file";
import { OrderType } from "types/declarations/enums.ts";
import { UnavailableDataPlaceholderText } from "constants/text";
import * as Excel from "exceljs";
import FileSaver from "file-saver";
import { models } from "types/api/viewModels.ts";
import { getSingleOrderGlobal } from "pages/OrderPages/utils/OrderDetailUtil.ts";
import { getOrderTypeDisplayLabel } from "utils/order";

export function convertOrderSummaryOrdersToExcelOrders(orders: models["OrderViewModel"][]) {
	return orders.map((order: models["OrderViewModel"]) => {
		const orderGlobals = getSingleOrderGlobal(order);
		return {
			"Account Number": checkForAndReplaceNullEmptyWhitespace(order.accountNumber),
			"PO Number": checkForAndReplaceNullEmptyWhitespace(order.poNumber),
			"Confirmation Number": checkForAndReplaceNullEmptyWhitespace(order.confirmationNumber),
			"Order Number": checkForAndReplaceNullEmptyWhitespace(order.orderNumber),
			"Order Status":
				order.orderType !== OrderType.BILLING_ONLY
					? checkForAndReplaceNullEmptyWhitespace(convertToTitleCase(order.status).toUpperCase())
					: UnavailableDataPlaceholderText,
			"Order Date": order.orderDate
				? formatDateToMMDDYYYY(new Date(order.orderDate))
				: UnavailableDataPlaceholderText,
			"Estimated Delivery Date":
				order.orderType !== OrderType.BILLING_ONLY
					? checkForAndReplaceNullEmptyWhitespace(order.eta)
					: UnavailableDataPlaceholderText,
			"Cabinet Count": order.counts?.cabinetCount ?? UnavailableDataPlaceholderText,
			"Cube Count": order.counts?.cubeCount ?? UnavailableDataPlaceholderText,
			"Accessory Count": order.counts?.accessoryCount ?? UnavailableDataPlaceholderText,
			Brand: checkForAndReplaceNullEmptyWhitespace(
				convertToTitleCase(orderGlobals?.brand?.description).toUpperCase()
			),
			"Designer Number": checkForAndReplaceNullEmptyWhitespace(order.designer?.designerNumber),
			"Door Style": checkForAndReplaceNullEmptyWhitespace(orderGlobals?.style?.description),
			Finish:
				order.orderType === OrderType.CUSTOM_COLOR
					? checkForAndReplaceNullEmptyWhitespace(
							`${orderGlobals?.finish?.customColor?.vendorDescription} - ${orderGlobals?.finish?.customColor?.colorDescription}`
						)
					: checkForAndReplaceNullEmptyWhitespace(orderGlobals?.finish?.description),
			"Job Name": checkForAndReplaceNullEmptyWhitespace(order.jobName),
			"Truck Number": checkForAndReplaceNullEmptyWhitespace(order.shippingDetails?.truckNumber),
			"Tracking Number": checkForAndReplaceNullEmptyWhitespace(order.shippingDetails?.trackingNumber),
			"Order Type": checkForAndReplaceNullEmptyWhitespace(getOrderTypeDisplayLabel(order.orderType).toUpperCase())
		};
	});
}

export async function generateMyOrdersExcel(orders: models["OrderViewModel"][]) {
	const workbook = new Excel.Workbook();
	const ordersWorksheet = workbook.addWorksheet();

	const csvOrders = convertOrderSummaryOrdersToExcelOrders(orders);

	if (csvOrders.length > 0) {
		ordersWorksheet.columns = Object.keys(csvOrders[0] ?? {}).map((col) => {
			return { key: col, header: col };
		});
		ordersWorksheet.addRows(csvOrders);
	}

	const filePrefix = getFileNameBasedOnCurrentDateTime("MyOrders");

	try {
		workbook.xlsx.writeBuffer().then((data) => {
			const blob = new Blob([data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
			});
			FileSaver.saveAs(blob, `${getFileNameBasedOnCurrentDateTime(filePrefix)}.xlsx`);
		});
	} catch (e) {
		console.error("Error trying to generate order list xlsx file", e);
	}
}
